import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { fetchOrder, markCustomerArrived, fetchMemberSavings } from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";
import { KioskReturn } from "@/components/KioskReturn";

/**
 * Customer-facing order confirmation.
 *
 * Adds a "📍 I've arrived to collect" button (collection orders only) that
 * pings the Hub. Polls every 5s for the staff's acknowledgement so the
 * customer sees "Thanks, we won't be long." come back.
 */
const OrderConfirmation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [arriving, setArriving] = useState(false);
    const pollRef = useRef(null);

    const refresh = () => fetchOrder(id).then(setOrder).catch(() => null);

    const [savings, setSavings] = useState(null);
    const memberId = order?.loyalty_member_id;
    useEffect(() => {
        if (memberId) fetchMemberSavings(memberId).then(setSavings).catch(() => {});
    }, [memberId]);

    useEffect(() => {
        fetchOrder(id).then(setOrder).catch(() => navigate("/"));
    }, [id, navigate]);

    // Poll for status + arrival_acknowledged_at while the order is still open
    useEffect(() => {
        if (!order) return;
        if (["collected", "cancelled"].includes(order.status)) return;
        // If customer has flagged arrival but staff hasn't acked, poll more often (3s)
        const isWaitingForAck = order.customer_arrived_at && !order.arrival_acknowledged_at;
        const interval = isWaitingForAck ? 3000 : 8000;
        pollRef.current = setInterval(refresh, interval);
        return () => clearInterval(pollRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.status, order?.customer_arrived_at, order?.arrival_acknowledged_at]);

    if (!order) {
        return <div className="min-h-screen bg-[var(--bg)] text-muted grid place-items-center">Loading order…</div>;
    }

    const isCollection = order.order_type !== "delivery";
    const isFinalised = ["collected", "cancelled"].includes(order.status);
    const hasArrived = !!order.customer_arrived_at;
    const isAcked = !!order.arrival_acknowledged_at;
    const showArriveBtn = isCollection && !isFinalised && !hasArrived && order.source !== "kiosk";
    // Delivery tracking (Gophr) — visible once the Hub taps "Book Courier"
    const gophr = order.gophr || null;
    const gophrBooked = !!(gophr && gophr.job_id);

    const onArrived = async () => {
        if (arriving) return;
        setArriving(true);
        try {
            const updated = await markCustomerArrived(id);
            setOrder(updated);
        } catch (e) {
            console.error(e);
            alert(e.response?.data?.detail || "Could not flag arrival — try again.");
        } finally { setArriving(false); }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" data-testid="order-confirmation">
            <header className="border-b border-[var(--border)] px-6 py-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Order placed</span>
            </header>
            <div className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">
                <div className="border border-[var(--border)] bg-[var(--surface)] p-8 md:p-12 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--gold)]" />
                    <p className="eyebrow">// Order #</p>
                    <h1
                        className="font-display text-[72px] md:text-[96px] mt-2 mb-4 text-gold leading-none"
                        data-testid="confirmation-order-number"
                    >
                        {order.order_number}
                    </h1>
                    <p className="text-muted mb-8 leading-relaxed">
                        {isCollection
                            ? "We've sent your order to the kitchen. Head to the counter for collection when called."
                            : gophrBooked
                                ? "Your driver is on the way. Live tracking below."
                                : "We've sent your order to the kitchen. A driver will be dispatched when it's ready."}
                    </p>

                    {memberId && ((order.discount || 0) > 0 || (savings?.total_saved || 0) > 0) && (
                        <div className="border border-[var(--gold)] bg-[var(--gold)]/10 p-5 mb-6 flex items-center gap-4" data-testid="confirmation-savings-badge">
                            <div className="text-[34px] leading-none text-gold" aria-hidden>✦</div>
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-1">// Naami Club savings</p>
                                {(order.discount || 0) > 0 && (
                                    <p className="font-display text-[22px] leading-tight" data-testid="confirmation-saved-this-order">
                                        You saved {formatMoney(order.discount)} on this order
                                    </p>
                                )}
                                {savings && savings.total_saved > 0 && (
                                    <p className="font-mono text-[12px] text-muted mt-1" data-testid="confirmation-saved-total">
                                        {formatMoney(savings.total_saved)} saved in total across {savings.orders} order{savings.orders === 1 ? "" : "s"}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Delivery tracking (Gophr) */}
                    {!isCollection && gophrBooked && (
                        <div className="border-2 border-[#3ea7ff] bg-[#3ea7ff]/10 p-5 mb-6" data-testid="confirmation-delivery-tracking">
                            <p className="eyebrow mb-2 text-[#3ea7ff]">// Live delivery</p>
                            <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Status</p>
                                    <p className="font-mono text-sm">{(gophr.status || "Confirmed").replace(/_/g, " ")}</p>
                                </div>
                                {gophr.delivery_eta && (
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">ETA</p>
                                        <p className="font-mono text-sm">{new Date(gophr.delivery_eta).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                                    </div>
                                )}
                                {gophr.courier_name && (
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Driver</p>
                                        <p className="font-mono text-sm">{gophr.courier_name}</p>
                                    </div>
                                )}
                                {gophr.courier_phone && (
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Contact</p>
                                        <a className="font-mono text-sm underline" href={`tel:${gophr.courier_phone}`}>{gophr.courier_phone}</a>
                                    </div>
                                )}
                            </div>
                            {gophr.tracking_url && (
                                <a
                                    href={gophr.tracking_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-primary w-full justify-center py-4 text-base"
                                    data-testid="confirmation-tracking-link"
                                >
                                    Open live tracker →
                                </a>
                            )}
                        </div>
                    )}

                    {!isCollection && !gophrBooked && (
                        <div className="border border-[var(--border)] p-4 mb-6 text-center" data-testid="confirmation-delivery-preparing">
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-1">
                                Preparing your order
                            </p>
                            <p className="text-sm text-muted">
                                We'll dispatch a driver as soon as it's bagged and ready. Refreshing…
                            </p>
                        </div>
                    )}

                    {/* Arrival flow */}
                    {showArriveBtn && (
                        <button
                            onClick={onArrived}
                            disabled={arriving}
                            className="btn-primary w-full justify-center text-base py-5 mb-6 disabled:opacity-40 animate-pulse"
                            data-testid="customer-arrived-btn"
                        >
                            📍 {arriving ? "Letting them know…" : "I've arrived to collect"}
                        </button>
                    )}
                    {hasArrived && !isAcked && !isFinalised && (
                        <div
                            className="border border-[var(--gold)] bg-[var(--gold)]/10 p-4 mb-6 text-center"
                            data-testid="customer-arrived-pending"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-1">
                                ✓ Arrival noted
                            </p>
                            <p className="text-sm text-muted">
                                Hold tight — the kitchen will be right with you.
                            </p>
                        </div>
                    )}
                    {hasArrived && isAcked && !isFinalised && (
                        <div
                            className="border-2 border-[var(--gold)] bg-[var(--gold)]/15 p-5 mb-6 text-center"
                            data-testid="customer-arrived-acked"
                        >
                            <p className="font-display text-[22px] text-gold leading-tight">
                                Thanks, we won't be long.
                            </p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted mt-2">
                                The team is on it
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-6 mb-8">
                        <Field label="Payment" value={order.pay_method === "card" ? "Card · paid" : "Cash · counter"} />
                        <Field label="Total" value={formatMoney(order.total)} />
                        {order.customer_name && <Field label="Name" value={order.customer_name} />}
                        <Field label="Source" value={order.source.toUpperCase()} />
                    </div>

                    <div className="border-t border-[var(--border)] pt-5 mb-3" />
                    <p className="eyebrow mb-3">// Items</p>
                    <div className="divide-y divide-[var(--border)]">
                        {order.lines.map((l) => (
                            <div key={`${l.item_id}-${(l.modifiers || []).map((m) => m.option_name).join("|")}`} className="py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-[11px] text-muted">[{l.brand_token}]</span>
                                        {l.category && (
                                            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold">
                                                {l.category}
                                            </span>
                                        )}
                                        <span>{l.name}</span>
                                    </div>
                                    {l.modifiers?.length > 0 && (
                                        <div className="font-mono text-[11px] text-muted pl-2 mt-0.5">
                                            {l.modifiers.map((m) => `+ ${m.option_name}`).join(" · ")}
                                        </div>
                                    )}
                                </div>
                                <div className="font-mono text-[12px] whitespace-nowrap">
                                    × {l.qty} · {formatMoney(l.price * l.qty)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {order.brand_notes && Object.keys(order.brand_notes).length > 0 && (
                        <>
                            <div className="border-t border-[var(--border)] mt-5 mb-3" />
                            <p className="eyebrow mb-2">// Kitchen notes (per brand)</p>
                            <div className="space-y-2">
                                {Object.entries(order.brand_notes).map(([bid, note]) => {
                                    const brandLine = order.lines.find((l) => l.brand_id === bid);
                                    if (!note || !brandLine) return null;
                                    return (
                                        <div key={bid} className="text-sm text-muted leading-relaxed border border-[var(--border)] p-2">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold block mb-1">
                                                {brandLine.brand_name}
                                            </span>
                                            {note}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {order.notes && !(order.brand_notes && Object.keys(order.brand_notes).length > 0) && (
                        <>
                            <div className="border-t border-[var(--border)] mt-5 mb-3" />
                            <p className="eyebrow mb-2">// Notes</p>
                            <p className="text-sm text-muted leading-relaxed">{order.notes}</p>
                        </>
                    )}
                </div>

                {order.source === "kiosk" ? <KioskReturn order={order} /> : (
                <div className="mt-8 flex flex-wrap gap-3">
                    {/* Both CTAs land on the multi-brand QR menu — customers should
                        never be dumped onto the walk-in kiosk after a QR / web order. */}
                    <Link to="/order" className="btn-secondary" data-testid="order-home-link">Home</Link>
                    <Link to="/order" className="btn-ghost" data-testid="order-again-link">Order again</Link>
                </div>
                )}
            </div>
        </div>
    );
};

const Field = ({ label, value }) => (
    <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">{label}</span>
        <span className="font-mono text-base">{value}</span>
    </div>
);

export default OrderConfirmation;
